import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  computeOrderTotals,
  formatAmount,
  formatRate,
  parseAmountToCents,
  type DiscountType,
  type Order,
  type OrderDiscount,
  type OrderItem,
} from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useCurrency } from '../lib/currency';
import { useToast } from '../hooks/useToast';
import { Button, Field, Input, Modal } from './ui';

/** "12.5" or "12,5" -> 1250 basis points; null when it is not a usable percentage. */
function parsePercentToBp(text: string, maxBp = 9_999): number | null {
  const normalised = text.trim().replace(',', '.');
  if (!/^\d{1,3}(\.\d{1,2})?$/.test(normalised)) return null;
  const bp = Math.round(Number(normalised) * 100);
  return bp >= 1 && bp <= maxBp ? bp : null;
}

/** The percent/fixed toggle, the amount and the reason — shared by both dialogs. */
function DiscountFields({
  type,
  onType,
  valueText,
  onValueText,
  reason,
  onReason,
  error,
  autoFocus = true,
}: {
  type: DiscountType;
  onType: (next: DiscountType) => void;
  valueText: string;
  onValueText: (next: string) => void;
  reason: string;
  onReason: (next: string) => void;
  error?: string;
  autoFocus?: boolean;
}) {
  const { symbol, decimals } = useCurrency();

  return (
    <>
      <div className="grid grid-cols-2 gap-2">
        {(['PERCENT', 'FIXED'] as const).map((candidate) => (
          <button
            key={candidate}
            type="button"
            onClick={() => {
              onType(candidate);
              onValueText('');
            }}
            className={clsx(
              'h-12 rounded-lg border text-sm font-medium transition',
              type === candidate
                ? 'border-accent bg-accent/12 text-accent-strong'
                : 'border-line text-ink-soft hover:border-line-strong hover:text-ink',
            )}
          >
            {candidate === 'PERCENT' ? 'Percentage (%)' : `Fixed amount (${symbol})`}
          </button>
        ))}
      </div>

      <Field label={type === 'PERCENT' ? 'Percent off' : 'Amount off'} error={error}>
        <div className="relative">
          <Input
            autoFocus={autoFocus}
            inputMode="decimal"
            className={clsx('text-lg', type === 'FIXED' ? 'pl-8' : 'pr-8')}
            value={valueText}
            onChange={(event) => onValueText(event.target.value)}
            placeholder={type === 'PERCENT' ? '10' : formatAmount(0, decimals)}
          />
          <span
            className={clsx(
              'absolute top-1/2 -translate-y-1/2 text-ink-faint',
              type === 'FIXED' ? 'left-3.5' : 'right-3.5',
            )}
          >
            {type === 'PERCENT' ? '%' : symbol}
          </span>
        </div>
      </Field>

      <Field label="Reason (optional)" hint="Shown in the audit log and the nightly giveaway report.">
        <Input
          value={reason}
          maxLength={200}
          onChange={(event) => onReason(event.target.value)}
          placeholder="Regular guest · Service recovery · Staff meal"
        />
      </Field>
    </>
  );
}

/**
 * Put a percentage or fixed-amount discount on the whole bill.
 *
 * The preview runs the same `computeOrderTotals` the Worker does, so the guest
 * sees exactly what will be charged — including how the discount lowers the
 * tax, which is worked out on the discounted amount.
 */
export function DiscountDialog({
  order,
  open,
  onClose,
}: {
  order: Order;
  open: boolean;
  onClose: () => void;
}) {
  const { money, symbol, decimals } = useCurrency();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [type, setType] = useState<DiscountType>('PERCENT');
  const [valueText, setValueText] = useState('');
  const [reason, setReason] = useState('');

  // Start from whatever is already on the bill, so the dialog edits rather than restarts.
  useEffect(() => {
    if (!open) return;
    const current = order.discountType;
    setType(current ?? 'PERCENT');
    setValueText(
      current === 'PERCENT'
        ? formatRate(order.discountValue)
        : current === 'FIXED'
          ? formatAmount(order.discountValue, decimals)
          : '',
    );
    setReason(order.discountReason ?? '');
    // Only re-seed when the dialog opens, not on every order refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const discount: OrderDiscount | null = useMemo(() => {
    if (!valueText.trim()) return null;
    if (type === 'PERCENT') {
      const bp = parsePercentToBp(valueText);
      return bp === null ? null : { type, value: bp };
    }
    try {
      const cents = parseAmountToCents(valueText);
      return cents >= 1 ? { type, value: cents } : null;
    } catch {
      return null;
    }
  }, [type, valueText]);

  const preview = useMemo(
    () =>
      computeOrderTotals(
        order.items.map((item) => ({
          quantity: item.quantity,
          unitPriceCents: item.unitPriceCents,
          taxType: item.taxType,
          taxRateBp: item.taxRateBp,
          status: item.status,
          modifiers: item.modifiers,
          // Discounts already on individual lines come off first, so the
          // preview has to carry them or the new total would be wrong.
          discount: item.discountType ? { type: item.discountType, value: item.discountValue } : null,
        })),
        {
          serviceChargeBp: order.serviceChargeBp,
          discount,
          taxServiceCharge: order.vatOnServiceCharge,
          roundingUnitCents: order.roundingUnitCents,
        },
      ),
    [order.items, order.serviceChargeBp, discount],
  );

  const coversWholeBill = discount !== null && preview.discountCents >= preview.subtotalCents;
  const belowPaid = discount !== null && preview.grandTotalCents < order.paidCents;
  const inputError = valueText.trim() && !discount
    ? type === 'PERCENT'
      ? 'Enter a percentage between 0.01 and 99.99'
      : 'Enter an amount greater than zero'
    : coversWholeBill
      ? 'A discount cannot cover the whole bill — use "Invite table" instead'
      : belowPaid
        ? 'That would bring the bill below what has already been paid'
        : undefined;
  const canApply = discount !== null && !inputError;

  const refresh = (updated: Order) => {
    queryClient.setQueryData(['order', updated.id], updated);
    void queryClient.invalidateQueries({ queryKey: ['tables'] });
  };
  const failed = (caught: unknown, fallback: string) =>
    toast.error(caught instanceof ApiError ? caught.message : fallback);

  const apply = useMutation({
    mutationFn: () => api.orders.setDiscount(order.id, { ...discount!, reason: reason.trim() || null }),
    onSuccess: (updated) => {
      refresh(updated);
      toast.success('Discount applied.');
      onClose();
    },
    onError: (caught) => failed(caught, 'Could not apply that discount.'),
  });

  const remove = useMutation({
    mutationFn: () => api.orders.clearDiscount(order.id),
    onSuccess: (updated) => {
      refresh(updated);
      toast.success('Discount removed.');
      onClose();
    },
    onError: (caught) => failed(caught, 'Could not remove the discount.'),
  });

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Discount"
      subtitle={`Table ${order.tableNumber} · Order #${order.orderNumber}`}
      size="md"
      footer={
        <>
          {order.discountType !== null ? (
            <Button
              variant="danger"
              className="mr-auto"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove discount
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={apply.isPending}
            disabled={!canApply}
            onClick={() => apply.mutate()}
          >
            Apply discount
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        <DiscountFields
          type={type}
          onType={setType}
          valueText={valueText}
          onValueText={setValueText}
          reason={reason}
          onReason={setReason}
          error={inputError}
        />

        <div className="rounded-card border border-line bg-canvas p-4">
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-ink-faint">Subtotal</dt>
              <dd className="tnum text-ink">{money(preview.subtotalCents)}</dd>
            </div>
            {preview.itemDiscountCents > 0 ? (
              <div className="flex justify-between">
                <dt className="text-ink-faint">Item discounts</dt>
                <dd className="tnum text-positive">−{money(preview.itemDiscountCents)}</dd>
              </div>
            ) : null}
            <div className="flex justify-between">
              <dt className="text-ink-faint">Bill discount</dt>
              <dd className="tnum text-positive">
                {preview.billDiscountCents > 0 ? `−${money(preview.billDiscountCents)}` : money(0)}
              </dd>
            </div>
            {preview.serviceChargeCents > 0 ? (
              <div className="flex justify-between">
                <dt className="text-ink-faint">Service {formatRate(preview.serviceChargeBp)}%</dt>
                <dd className="tnum text-ink">{money(preview.serviceChargeCents)}</dd>
              </div>
            ) : null}
            <div className="flex justify-between">
              <dt className="text-ink-faint">Tax (on discounted amount)</dt>
              <dd className="tnum text-ink">{money(preview.taxCents)}</dd>
            </div>
          </dl>
          <div className="my-3 h-px bg-line" />
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-ink-faint">New total</span>
            <span className="tnum font-display text-2xl text-accent-strong">
              {money(preview.grandTotalCents)}
            </span>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * Money off one dish — a complaint, a long wait, or a dish put on the house.
 *
 * Deliberately a separate dialog from the bill-level one above: the preview
 * that matters here is the line, not the order, and comping one dish is a
 * one-tap job rather than a form. The item's discount comes off before any
 * bill-level discount, which is then worked out on what is left.
 */
export function ItemDiscountDialog({
  order,
  item,
  open,
  onClose,
}: {
  order: Order;
  item: OrderItem | null;
  open: boolean;
  onClose: () => void;
}) {
  const { money, decimals } = useCurrency();
  const toast = useToast();
  const queryClient = useQueryClient();

  const [type, setType] = useState<DiscountType>('PERCENT');
  const [valueText, setValueText] = useState('');
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open || !item) return;
    setType(item.discountType ?? 'PERCENT');
    setValueText(
      item.discountType === 'PERCENT'
        ? formatRate(item.discountValue)
        : item.discountType === 'FIXED'
          ? formatAmount(item.discountValue, decimals)
          : '',
    );
    setReason(item.discountReason ?? '');
    // Only re-seed when the dialog opens, not on every order refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const discount: OrderDiscount | null = useMemo(() => {
    if (!valueText.trim()) return null;
    if (type === 'PERCENT') {
      const bp = parsePercentToBp(valueText, 10_000);
      return bp === null ? null : { type, value: bp };
    }
    try {
      const cents = parseAmountToCents(valueText);
      return cents >= 1 ? { type, value: cents } : null;
    } catch {
      return null;
    }
  }, [type, valueText]);

  const refresh = (updated: Order) => {
    queryClient.setQueryData(['order', updated.id], updated);
    void queryClient.invalidateQueries({ queryKey: ['tables'] });
  };
  const failed = (caught: unknown, fallback: string) =>
    toast.error(caught instanceof ApiError ? caught.message : fallback);

  const apply = useMutation({
    mutationFn: (value: OrderDiscount) =>
      api.orders.setItemDiscount(order.id, item!.id, { ...value, reason: reason.trim() || null }),
    onSuccess: (updated) => {
      refresh(updated);
      toast.success('Discount applied to the item.');
      onClose();
    },
    onError: (caught) => failed(caught, 'Could not discount that item.'),
  });

  const remove = useMutation({
    mutationFn: () => api.orders.clearItemDiscount(order.id, item!.id),
    onSuccess: (updated) => {
      refresh(updated);
      toast.success('Item discount removed.');
      onClose();
    },
    onError: (caught) => failed(caught, 'Could not remove that discount.'),
  });

  if (!item) return null;

  /* The line as it is now, and what the discount being typed would make of it.
     `lineSubtotalCents` is the gross line, so the preview is independent of any
     bill-level discount — which is also how the server applies it. */
  const grossCents = item.lineSubtotalCents;
  const previewDiscountCents =
    discount === null
      ? 0
      : Math.min(
          discount.type === 'PERCENT' ? Math.round((grossCents * discount.value) / 10_000) : discount.value,
          grossCents,
        );
  const inputError =
    valueText.trim() && !discount
      ? type === 'PERCENT'
        ? 'Enter a percentage between 0.01 and 100'
        : 'Enter an amount greater than zero'
      : undefined;
  const canApply = discount !== null && !inputError;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Discount item"
      subtitle={`${item.quantity}× ${item.nameSnapshot} · Table ${order.tableNumber}`}
      size="md"
      footer={
        <>
          {item.discountType !== null ? (
            <Button
              variant="danger"
              className="mr-auto"
              loading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Remove discount
            </Button>
          ) : null}
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            loading={apply.isPending && apply.variables?.value !== 10_000}
            disabled={!canApply}
            onClick={() => discount && apply.mutate(discount)}
          >
            Apply discount
          </Button>
        </>
      }
    >
      <div className="space-y-5">
        {/* Comping a dish outright is the common case, so it is one tap. */}
        <button
          type="button"
          onClick={() => apply.mutate({ type: 'PERCENT', value: 10_000 })}
          disabled={apply.isPending}
          className="flex w-full items-center justify-between gap-3 rounded-lg border border-accent/40 bg-accent/8 px-4 py-3 text-left transition hover:border-accent disabled:opacity-50"
        >
          <span>
            <span className="block text-sm font-medium text-ink">On the house — 100% off</span>
            <span className="mt-0.5 block text-xs text-ink-faint">
              The dish stays on the bill at zero, so the guest can see it was complimentary.
            </span>
          </span>
          <span className="tnum shrink-0 font-display text-lg text-accent-strong">−{money(grossCents)}</span>
        </button>

        <DiscountFields
          type={type}
          onType={setType}
          valueText={valueText}
          onValueText={setValueText}
          reason={reason}
          onReason={setReason}
          error={inputError}
          autoFocus={false}
        />

        <div className="rounded-card border border-line bg-canvas p-4">
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between">
              <dt className="text-ink-faint">Line total</dt>
              <dd className="tnum text-ink">{money(grossCents)}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-ink-faint">Discount</dt>
              <dd className="tnum text-positive">
                {previewDiscountCents > 0 ? `−${money(previewDiscountCents)}` : money(0)}
              </dd>
            </div>
          </dl>
          <div className="my-3 h-px bg-line" />
          <div className="flex items-baseline justify-between">
            <span className="text-sm text-ink-faint">Guest pays for this line</span>
            <span className="tnum font-display text-2xl text-accent-strong">
              {money(grossCents - previewDiscountCents)}
            </span>
          </div>
          <p className="mt-2 text-xs text-ink-faint">
            Tax and service are recalculated on the discounted amount, and any discount on the whole
            bill is worked out on what is left.
          </p>
        </div>
      </div>
    </Modal>
  );
}
